import React from 'react';
import { Bot, Folder, PanelLeft, PlugZap, Radar, Radio, Search, Sparkles, SquarePen, Users } from 'lucide-react';

// The dashboard-icons CDN is missing marks for several of these (HubSpot,
// Snowflake, QuickBooks, Intercom), which rendered as broken images. Resolving
// through each brand's own domain is the pattern used elsewhere in the project
// and covers every tool we reference.
const TOOL_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  notion: 'notion.so',
  salesforce: 'salesforce.com',
  linear: 'linear.app',
  hubspot: 'hubspot.com',
  snowflake: 'snowflake.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  intercom: 'intercom.com',
  ahrefs: 'ahrefs.com',
  github: 'github.com',
  sentry: 'sentry.io',
  mailchimp: 'mailchimp.com',
  granola: 'granola.ai',
  // AI surfaces the connections are exposed to.
  giga: 'getgiga.com',
  chatgpt: 'openai.com',
  claude: 'claude.ai',
  // grok.com carries Grok's own mark; x.ai's favicon is the plain X wordmark,
  // which reads as SpaceX rather than the product.
  grok: 'grok.com'
};
export const toolLogo = (slug: string) => `https://www.google.com/s2/favicons?domain=${TOOL_DOMAINS[slug] ?? `${slug}.com`}&sz=64`;

/**
 * Shared chrome for every real-product screenshot on the page. One frame means
 * Connections, the platform primitives and the brain all read as the same
 * application rather than four unrelated illustrations.
 */
export function AppChrome({
  children,
  sidebar = true,
  active,
  className = ''
}: {
  children: React.ReactNode;
  sidebar?: boolean;
  active?: string;
  className?: string;
}) {
  // No browser bar: the fake URL and window chrome added a frame around the
  // frame without telling anyone anything they didn't already know.
  // Two-part shadow: a 1px contact edge keeps the frame crisp against cream,
  // while the long soft cast gives it real elevation rather than a flat drop.
  return <div className={`overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.05),0_8px_24px_-12px_rgba(43,33,26,0.18),0_40px_100px_-40px_rgba(43,33,26,0.45)] ${className}`} data-id="element-191" data-mp="1qxs2cw66d2as">
      <div className="flex min-h-[420px]" data-id="element-192" data-mp="1hxdcrkm6h11h">
        {sidebar && <AppSidebar active={active} data-id="element-193" data-mp="1huqyl24s7naa" />}
        <div className="min-w-0 flex-1 bg-white" data-id="element-194" data-mp="1mveykdaznegn">{children}</div>
      </div>
    </div>;
}
const SIDEBAR_CHATS = ['Inbox catch-up', 'Renewal risk', 'Board deck notes'];
function AppSidebar({
  active
}: {
  active?: string;
}) {
  return <aside className="hidden w-[196px] shrink-0 flex-col border-r border-[#EFEAE1] bg-[#FCFAF6] px-2.5 py-3 lg:flex" data-id="element-195" data-mp="2emkq45cn83ub">
      <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-196" data-mp="20pfnygg1vp4c">
        <span className="flex items-center gap-2" data-id="element-197" data-mp="25l4q8rjgfk4c">
          <span className="flex size-6 items-center justify-center rounded-lg bg-[#134E4A] text-[10px] font-bold text-white" data-id="element-198" data-mp="p867m2s7xd79">
            BW
          </span>
          <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-199" data-mp="u8u5n8ev6uaz">
            Brightwave
          </span>
        </span>
        <PanelLeft size={13} className="text-neutral-300" data-id="element-200" data-mp="mpkxtjm0b3o1" />
      </div>
      <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#E7E0D4] bg-white px-2.5 py-1.5" data-id="element-201" data-mp="ho5b7n46kp3j">
        <SquarePen size={12} className="text-neutral-400" data-id="element-202" data-mp="vnn34bcrwih4" />
        <span className="text-[12px] font-semibold text-[#2B211A]" data-id="element-203" data-mp="qp3ice9x1cqc">
          New chat
        </span>
      </div>
      <div className="mb-1 flex items-center gap-2 rounded-lg bg-white px-2 py-1.5 ring-1 ring-[#EFEAE1]" data-id="element-204" data-mp="30ka1yqv8zy9">
        <Search size={11} className="text-neutral-300" data-id="element-205" data-mp="3v54ms6fn9zxa" />
        <span className="text-[11.5px] text-neutral-400" data-id="element-206" data-mp="cpals2bpk2ag">Search</span>
      </div>
      <SidebarGroup label="Manage" items={MANAGE_ITEMS} active={active} data-id="element-207" data-mp="7yu5magi62ke" />
      <SidebarGroup label="Brain" items={BRAIN_ITEMS} active={active} data-id="element-208" data-mp="7xj3ef2ph2ve" />
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-209" data-mp="3gdm1bt40nd6j">
        Chats
      </p>
      <ul data-id="element-210" data-mp="3bfarhidq12ob">
        {SIDEBAR_CHATS.map(c => <li key={c} className="truncate rounded-lg px-2 py-[5px] text-[12px] font-medium text-neutral-500" data-id="element-211" data-mp="3p2jiojefruru">
            {c}
          </li>)}
      </ul>
    </aside>;
}
const BRAIN_ITEMS = [{
  label: 'Tracks',
  icon: Folder
}, {
  label: 'Scans',
  icon: Radar
}, {
  label: 'Observations',
  icon: Sparkles
}];
const MANAGE_ITEMS = [{
  label: 'Assistants',
  icon: Bot
}, {
  label: 'Pulses',
  icon: Radio
}, {
  label: 'Connections',
  icon: PlugZap
}, {
  label: 'Groups',
  icon: Users
}];
function SidebarGroup({
  label,
  items,
  active
}: {
  label: string;
  items: Array<{
    label: string;
    icon: React.ElementType;
  }>;
  active?: string;
}) {
  return <>
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-212" data-mp="ljq7gd94ri0u">
        {label}
      </p>
      <ul data-id="element-213" data-mp="2ndh2cwqx8bxl">
        {items.map(item => {
        const isActive = active === item.label;
        return <li key={item.label} className={`flex items-center gap-2 rounded-lg px-2 py-[5px] text-[12px] ${isActive ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-500'}`} data-id="element-214" data-mp="2sbil1gkfq98j">
              <item.icon size={11} strokeWidth={1.9} className={`shrink-0 ${isActive ? 'text-[#2B211A]' : 'text-neutral-300'}`} data-id="element-215" data-mp="14pnx679fc4kw" />
              <span className="truncate" data-id="element-216" data-mp="10pdvv7wku2cd">{item.label}</span>
            </li>;
      })}
      </ul>
    </>;
}